import {
  EntitlementFeatureType,
  updateComplianceItem,
  updateServiceRequest,
  useHasPaidForFeature,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  SingleFileUploader,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, { MIXPANEL_SOURCE, MIXPANEL_SURFACE_AREA } from '@commenda/mixpanel';
import { FileBasic } from '@commenda/types/src/file/types';
import { AppType, useApp } from '@commenda/utils';
import {
  ComplianceStatus,
  LegacyDocumentType,
  ServiceRequestStatus,
} from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';

const CompleteFilingModal = ({
  isOpen,
  onClose,
  onComplete,
  filingId,
  companyId,
  serviceRequestId,
}: {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  filingId: string;
  companyId: number;
  serviceRequestId?: string;
}) => {
  const [file, setFile] = useState<FileBasic | null>(null);
  const { appType } = useApp();

  const isAccountantApp = appType === AppType.ACCOUNTANT;

  const { data: hasPaidForCompliance } = useHasPaidForFeature({
    companyId,
    complianceId: filingId,
    featureType: EntitlementFeatureType.COMPLIANCE,
  });

  const isSubmitting = useToggle(false);

  const { setNotification } = useNotification();

  const close = () => {
    setFile(null);
    onClose();
  };

  const onCompleteFiling = async () => {
    isSubmitting.on();

    if (serviceRequestId) {
      // we also need to update the service request task status to completed
      const resSR = await updateServiceRequest({
        id: serviceRequestId,
        data: {
          status: ServiceRequestStatus.COMPLETED,
        },
      });

      if (!resSR.ok) {
        setNotification({
          title: 'Error',
          message: 'Failed to complete the service request',
          valid: false,
          show: true,
        });
        isSubmitting.off();
        return;
      }
    }

    const res = await updateComplianceItem(filingId, {
      status: ComplianceStatus.COMPLETED,
      acknowledgements: [
        {
          documentType: LegacyDocumentType.Other,
          fileName: file?.fileName,
          fileUrl: file?.fileUrl,
        },
      ],
    });

    if (!res.success) {
      setNotification({
        title: 'Error',
        message: 'Failed to complete filing',
        valid: false,
        show: true,
      });

      isSubmitting.off();
      return;
    }

    setNotification({
      title: 'Success',
      message: 'Filing has been marked as completed.',
      valid: true,
      show: true,
    });

    if (file?.fileName) {
      mixpanel.track('OBLIGATION_ACKNOWLEDGEMENT_UPLOADED', {
        source: MIXPANEL_SOURCE.CALENDAR,
        surface_area: MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
        company_id: companyId,
        compliance_id: filingId,
      });
    }

    await onComplete();
    isSubmitting.off();
    close();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start border-b-0 pb-0">
          <DialogTitle>Complete this filing</DialogTitle>
          <DialogDescription>
            If you have completed this filing already, upload a receipt to mark
            it as complete.{' '}
            {hasPaidForCompliance?.hasAccess && !isAccountantApp
              ? 'Your accounting partner will be notified.'
              : ''}
            <br />
            <br />
            Once completed, this action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Upload receipt</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <div onClick={(e) => e.stopPropagation()}>
              <SingleFileUploader
                companyId={companyId}
                documentType={LegacyDocumentType.Other}
                file={file}
                setFile={setFile}
                accept={{
                  'application/pdf': ['.pdf', '.xlsx', '.docx', '.csv'],
                  'image/*': ['.png', '.jpg', '.jpeg'],
                }}
              />
            </div>
          </FieldWrapper>
        </div>
        <DialogFooter className="border-t-0">
          <Button variant="secondary" onClick={close} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onCompleteFiling}
            size="md"
            disabled={!file || isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteFilingModal;
